<script>
import { useAlert } from 'dashboard/composables';
import inboxMixin from 'shared/mixins/inboxMixin';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import SettingsAccordion from 'dashboard/components-next/Settings/SettingsAccordion.vue';
import ImapSettings from '../ImapSettings.vue';
import SmtpSettings from '../SmtpSettings.vue';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TextArea from 'next/textarea/TextArea.vue';
import WhatsappReauthorize from '../channels/whatsapp/Reauthorize.vue';
import { sanitizeAllowedDomains } from 'dashboard/helper/URLHelper';
import inboxesAPI from 'dashboard/api/inboxes';

export default {
  components: {
    SettingsFieldSection,
    SettingsToggleSection,
    SettingsAccordion,
    ImapSettings,
    SmtpSettings,
    NextButton,
    TextArea,
    WhatsappReauthorize,
  },
  mixins: [inboxMixin],
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      hmacMandatory: false,
      allowMobileWebview: false,
      whatsAppInboxAPIKey: '',
      isRequestingReauthorization: false,
      isSyncingTemplates: false,
      allowedDomains: '',
      isUpdatingAllowedDomains: false,
      isSettingDefaults: false,
      evolutionStatusMessage: '',
      evolutionQrCode: '',
      isFetchingEvolution: false,
      automationDeliveryPolicy: {
        mode: 'immediate',
        inactivity_minutes: 30,
        maximum_wait_minutes: 120,
        notify_assignee: true,
      },
      isSavingAutomationDeliveryPolicy: false,
    };
  },
  validations: {
    whatsAppInboxAPIKey: { required },
  },
  computed: {
    isEmbeddedSignupWhatsApp() {
      return this.inbox.provider_config?.source === 'embedded_signup';
    },
    whatsappAppId() {
      return window.lumeratecConfig?.whatsappAppId;
    },
    isForwardingEnabled() {
      return !!this.inbox.forwarding_enabled;
    },
    isEvolutionAPI() {
      return (
        this.inbox.additional_attributes &&
        this.inbox.additional_attributes.integration === 'evolution'
      );
    },
    supportsAutomationDeliveryPolicy() {
      const integration = String(
        this.inbox.additional_attributes?.integration || ''
      ).toLowerCase();
      return (
        this.isAWhatsAppChannel ||
        (this.isAPIInbox &&
          ['evolution', 'billing_simulation'].includes(integration))
      );
    },
  },
  watch: {
    inbox() {
      this.setDefaults();
    },
    allowMobileWebview() {
      if (!this.isSettingDefaults) this.handleMobileWebviewFlag();
    },
    hmacMandatory() {
      if (!this.isSettingDefaults && this.isAWebWidgetInbox)
        this.handleHmacFlag();
    },
  },
  mounted() {
    this.setDefaults();
  },
  methods: {
    setDefaults() {
      this.isSettingDefaults = true;
      this.hmacMandatory = this.inbox.hmac_mandatory || false;
      this.allowMobileWebview = (
        this.inbox.selected_feature_flags || []
      ).includes('allow_mobile_webview');
      this.allowedDomains = this.inbox.allowed_domains || '';
      this.automationDeliveryPolicy = {
        mode: 'immediate',
        inactivity_minutes: 30,
        maximum_wait_minutes: 120,
        notify_assignee: true,
        ...(this.inbox.automation_delivery_policy || {}),
      };
      this.$nextTick(() => {
        this.isSettingDefaults = false;
      });
    },
    handleHmacFlag() {
      this.updateInbox();
    },
    async updateInbox() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            hmac_mandatory: this.hmacMandatory,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async handleMobileWebviewFlag() {
      try {
        const currentFlags = this.inbox.selected_feature_flags || [];
        const selectedFlags = this.allowMobileWebview
          ? [...currentFlags, 'allow_mobile_webview']
          : currentFlags.filter(f => f !== 'allow_mobile_webview');

        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            selected_feature_flags: selectedFlags,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async updateAllowedDomains() {
      this.isUpdatingAllowedDomains = true;
      const sanitizedAllowedDomains = sanitizeAllowedDomains(
        this.allowedDomains
      );
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            allowed_domains: sanitizedAllowedDomains,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        this.allowedDomains = sanitizedAllowedDomains;
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.isUpdatingAllowedDomains = false;
      }
    },
    async updateWhatsAppInboxAPIKey() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {},
        };

        payload.channel.provider_config = {
          ...this.inbox.provider_config,
          api_key: this.whatsAppInboxAPIKey,
        };

        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async saveAutomationDeliveryPolicy() {
      const inactivityMinutes = Math.min(
        1440,
        Math.max(
          1,
          Number(this.automationDeliveryPolicy.inactivity_minutes) || 30
        )
      );
      const maximumWaitMinutes = Math.min(
        1440,
        Math.max(
          inactivityMinutes,
          Number(this.automationDeliveryPolicy.maximum_wait_minutes) || 120
        )
      );
      this.isSavingAutomationDeliveryPolicy = true;
      this.automationDeliveryPolicy = {
        ...this.automationDeliveryPolicy,
        inactivity_minutes: inactivityMinutes,
        maximum_wait_minutes: maximumWaitMinutes,
      };
      try {
        await this.$store.dispatch('inboxes/updateInbox', {
          id: this.inbox.id,
          formData: false,
          automation_delivery_policy: this.automationDeliveryPolicy,
          channel: {},
        });
        useAlert(this.$t('INBOX_ALERTS.AUTO_DELIVERY_POLICY_UPDATED'));
      } catch (error) {
        useAlert(this.$t('INBOX_ALERTS.AUTO_DELIVERY_POLICY_ERROR'));
      } finally {
        this.isSavingAutomationDeliveryPolicy = false;
      }
    },
    async handleReconfigure() {
      if (this.$refs.whatsappReauth) {
        await this.$refs.whatsappReauth.requestAuthorization();
      }
    },
    async syncTemplates() {
      this.isSyncingTemplates = true;
      try {
        await this.$store.dispatch('inboxes/syncTemplates', this.inbox.id);
        useAlert(
          this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_SUCCESS')
        );
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.isSyncingTemplates = false;
      }
    },
    async checkEvolutionStatus() {
      this.isFetchingEvolution = true;
      try {
        const response = await inboxesAPI.evolutionStatus(this.inbox.id);
        this.evolutionStatusMessage =
          response.data.instance?.state ||
          response.data.instance?.status ||
          JSON.stringify(response.data);
      } catch (error) {
        useAlert(this.$t('INBOX_ALERTS.EVOLUTION_STATUS_ERROR'));
      } finally {
        this.isFetchingEvolution = false;
      }
    },
    async reconnectEvolution() {
      this.isFetchingEvolution = true;
      this.evolutionQrCode = '';
      try {
        const response = await inboxesAPI.evolutionConnect(this.inbox.id);
        if (response.data.base64) {
          this.evolutionQrCode = response.data.base64;
          this.evolutionStatusMessage = this.$t(
            'INBOX_ALERTS.EVOLUTION_WAITING_FOR_QR'
          );
        } else {
          this.evolutionStatusMessage =
            response.data.instance?.state ||
            this.$t('INBOX_MGMT.WHATSAPP_CONNECTION.CONNECTED_OR_ERROR');
        }
      } catch (error) {
        useAlert(this.$t('INBOX_ALERTS.EVOLUTION_RECONNECT_ERROR'));
      } finally {
        this.isFetchingEvolution = false;
      }
    },
    async logoutEvolution() {
      this.isFetchingEvolution = true;
      this.evolutionQrCode = '';
      try {
        await inboxesAPI.evolutionLogout(this.inbox.id);
        this.evolutionStatusMessage = this.$t(
          'INBOX_ALERTS.EVOLUTION_LOGOUT_SUCCESS'
        );
      } catch (error) {
        useAlert(this.$t('INBOX_ALERTS.EVOLUTION_LOGOUT_ERROR'));
      } finally {
        this.isFetchingEvolution = false;
      }
    },
  },
};
</script>

<template>
  <SettingsToggleSection
    v-if="supportsAutomationDeliveryPolicy"
    :header="$t('INBOX_MGMT.AUTOMATION_DELIVERY.TITLE')"
    :description="$t('INBOX_MGMT.AUTOMATION_DELIVERY.DESCRIPTION')"
    hide-toggle
    class="mb-6"
  >
    <template #editor>
      <div class="space-y-4">
        <label class="flex gap-2 items-start cursor-pointer">
          <input
            v-model="automationDeliveryPolicy.mode"
            type="radio"
            value="immediate"
          />
          <span class="text-sm text-n-slate-12">
            <span class="block font-medium">{{
              $t('INBOX_MGMT.AUTOMATION_DELIVERY.IMMEDIATE')
            }}</span>
            {{ $t('INBOX_MGMT.AUTOMATION_DELIVERY.IMMEDIATE_DESCRIPTION') }}
          </span>
        </label>
        <label class="flex gap-2 items-start cursor-pointer">
          <input
            v-model="automationDeliveryPolicy.mode"
            type="radio"
            value="wait_for_inactivity"
          />
          <span class="text-sm text-n-slate-12">
            <span class="block font-medium">
              {{ $t('INBOX_MGMT.AUTOMATION_DELIVERY.WAIT_FOR_INACTIVITY') }}
            </span>
            {{ $t('INBOX_MGMT.AUTOMATION_DELIVERY.WAIT_DESCRIPTION') }}
          </span>
        </label>
        <div
          v-if="automationDeliveryPolicy.mode === 'wait_for_inactivity'"
          class="grid gap-3 sm:grid-cols-2"
        >
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.AUTOMATION_DELIVERY.INACTIVITY_MINUTES') }}
            <input
              v-model.number="automationDeliveryPolicy.inactivity_minutes"
              type="number"
              min="1"
              max="1440"
              class="mt-1 w-full rounded-lg border border-n-strong bg-n-alpha-2 px-3 py-2 text-n-slate-12"
            />
          </label>
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.AUTOMATION_DELIVERY.MAX_WAIT_MINUTES') }}
            <input
              v-model.number="automationDeliveryPolicy.maximum_wait_minutes"
              type="number"
              min="1"
              max="1440"
              class="mt-1 w-full rounded-lg border border-n-strong bg-n-alpha-2 px-3 py-2 text-n-slate-12"
            />
          </label>
        </div>
        <label
          class="flex gap-2 items-center text-sm text-n-slate-12 cursor-pointer"
        >
          <input
            v-model="automationDeliveryPolicy.notify_assignee"
            type="checkbox"
          />
          {{ $t('INBOX_MGMT.AUTOMATION_DELIVERY.NOTIFY_ASSIGNEE') }}
        </label>
        <div class="flex justify-end">
          <NextButton
            :is-loading="isSavingAutomationDeliveryPolicy"
            @click="saveAutomationDeliveryPolicy"
          >
            {{ $t('INBOX_MGMT.AUTOMATION_DELIVERY.SAVE') }}
          </NextButton>
        </div>
      </div>
    </template>
  </SettingsToggleSection>
  <div v-else-if="isALineChannel">
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.ADD.LINE_CHANNEL.API_CALLBACK.TITLE')"
      :help-text="$t('INBOX_MGMT.ADD.LINE_CHANNEL.API_CALLBACK.SUBTITLE')"
    >
      <woot-code :script="inbox.callback_webhook_url" lang="html" />
    </SettingsFieldSection>
  </div>
  <div v-else-if="isAWebWidgetInbox">
    <div class="space-y-4">
      <SettingsToggleSection
        :header="$t('INBOX_MGMT.SETTINGS_POPUP.ALLOWED_DOMAINS.TITLE')"
        :description="
          $t('INBOX_MGMT.SETTINGS_POPUP.ALLOWED_DOMAINS.DESCRIPTION')
        "
        hide-toggle
      >
        <template #editor>
          <TextArea
            v-model="allowedDomains"
            :placeholder="
              $t('INBOX_MGMT.SETTINGS_POPUP.ALLOWED_DOMAINS.PLACEHOLDER')
            "
            auto-height
            resize
            class="w-full [&>div]:!bg-transparent [&>div]:!border-none [&>div]:!border-0 [&>div]:px-0 [&>div]:pb-0 [&>div]:pt-0"
          />
          <div class="mt-3 flex justify-end">
            <NextButton
              :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
              :is-loading="isUpdatingAllowedDomains"
              @click="updateAllowedDomains"
            />
          </div>
        </template>
      </SettingsToggleSection>
      <SettingsToggleSection
        v-model="allowMobileWebview"
        :header="$t('INBOX_MGMT.SETTINGS_POPUP.ALLOW_MOBILE_WEBVIEW.LABEL')"
        :description="
          $t('INBOX_MGMT.SETTINGS_POPUP.ALLOW_MOBILE_WEBVIEW.SUBTITLE')
        "
      />
    </div>

    <SettingsAccordion
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.TITLE')"
      class="mt-6"
    >
      <SettingsToggleSection
        :header="$t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.TITLE')"
        :description="
          $t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.DESCRIPTION')
        "
        hide-toggle
      >
        <template #editor>
          <p class="mb-1 text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.SECRET_KEY') }}
          </p>
          <woot-code :script="inbox.hmac_token" />
          <p class="mt-1.5 text-label-small text-n-slate-11">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.HMAC_DESCRIPTION') }}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.lumeratec.com/docs/product/channels/live-chat/sdk/identity-validation/"
              class="text-n-blue-11 hover:underline text-label-small"
            >
              {{
                $t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.VIEW_DOCS')
              }}
            </a>
          </p>
        </template>
      </SettingsToggleSection>

      <SettingsToggleSection
        v-model="hmacMandatory"
        :header="
          $t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.REQUIRE_LABEL')
        "
        :description="
          $t(
            'INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.REQUIRE_DESCRIPTION'
          )
        "
      />
    </SettingsAccordion>
  </div>
  <div v-else-if="isAPIInbox">
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_IDENTIFIER')"
      :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_IDENTIFIER_SUB_TEXT')"
    >
      <woot-code :script="inbox.inbox_identifier" />
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_VERIFICATION')"
      :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_DESCRIPTION')"
    >
      <woot-code :script="inbox.hmac_token" />
    </SettingsFieldSection>
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_VERIFICATION')"
      :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_DESCRIPTION')"
    >
      <div class="flex gap-2 items-center">
        <input
          id="hmacMandatory"
          v-model="hmacMandatory"
          type="checkbox"
          @change="handleHmacFlag"
        />
        <label for="hmacMandatory" class="text-body-main text-n-slate-12">
          {{ $t('INBOX_MGMT.EDIT.ENABLE_HMAC.LABEL') }}
        </label>
      </div>
    </SettingsFieldSection>
  </div>
  <div v-else-if="isAnEmailChannel">
    <div>
      <SettingsFieldSection
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.FORWARD_EMAIL_TITLE')"
        :help-text="
          isForwardingEnabled
            ? $t('INBOX_MGMT.SETTINGS_POPUP.FORWARD_EMAIL_SUB_TEXT')
            : ''
        "
      >
        <woot-code
          v-if="isForwardingEnabled"
          :script="inbox.forward_to_email"
        />
        <div
          v-else
          class="py-2 px-3 bg-n-amber-3 outline-n-amber-4 text-n-amber-11 outline outline-1 -outline-offset-1 rounded-xl"
        >
          <p class="text-body-para mb-0">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.FORWARD_EMAIL_NOT_CONFIGURED') }}
          </p>
        </div>
      </SettingsFieldSection>
    </div>
    <ImapSettings :inbox="inbox" />
    <SmtpSettings v-if="inbox.imap_enabled" :inbox="inbox" />
  </div>
  <div v-else-if="isAWhatsAppChannel">
    <div v-if="inbox.provider_config">
      <!-- Embedded Signup Section -->
      <template v-if="isEmbeddedSignupWhatsApp">
        <SettingsFieldSection
          v-if="whatsappAppId"
          :label="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_EMBEDDED_SIGNUP_TITLE')
          "
          :help-text="`${$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_EMBEDDED_SIGNUP_SUBHEADER')} ${$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_EMBEDDED_SIGNUP_DESCRIPTION')}`"
        >
          <div class="flex flex-col gap-1 items-start">
            <NextButton @click="handleReconfigure">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_RECONFIGURE_BUTTON') }}
            </NextButton>
          </div>
        </SettingsFieldSection>
      </template>

      <!-- Manual Setup Section -->
      <template v-else>
        <SettingsFieldSection
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_WEBHOOK_TITLE')"
          :help-text="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_WEBHOOK_SUBHEADER')
          "
        >
          <woot-code :script="inbox.provider_config.webhook_verify_token" />
        </SettingsFieldSection>
        <SettingsFieldSection
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_TITLE')"
          :help-text="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_SUBHEADER')
          "
        >
          <woot-code :script="inbox.provider_config.api_key" />
        </SettingsFieldSection>
        <SettingsFieldSection
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_TITLE')"
          :help-text="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_SUBHEADER')
          "
        >
          <div
            class="flex flex-1 justify-between items-center whatsapp-settings--content"
          >
            <woot-input
              v-model="whatsAppInboxAPIKey"
              type="text"
              class="flex-1 mr-2 [&>input]:!mb-0"
              :placeholder="
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_PLACEHOLDER'
                )
              "
            />
            <NextButton
              :disabled="v$.whatsAppInboxAPIKey.$invalid"
              @click="updateWhatsAppInboxAPIKey"
            >
              {{
                $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON')
              }}
            </NextButton>
          </div>
        </SettingsFieldSection>
      </template>
      <SettingsFieldSection
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_TITLE')"
        :help-text="
          $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_SUBHEADER')
        "
      >
        <NextButton :disabled="isSyncingTemplates" @click="syncTemplates">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_BUTTON') }}
        </NextButton>
      </SettingsFieldSection>
    </div>
    <WhatsappReauthorize
      v-if="isEmbeddedSignupWhatsApp"
      ref="whatsappReauth"
      :inbox="inbox"
      class="hidden"
    />
  </div>
</template>

<style lang="scss" scoped>
.whatsapp-settings--content {
  :deep(input) {
    margin-bottom: 0;
  }
}
</style>
